/**
 * Usage Insights Component
 * Displays personalized usage insights in natural language
 */

'use client';

import Link from 'next/link';
import { TrendingUp, BookOpen, Wrench, Calendar, Award } from 'lucide-react';

interface UsageInsightsProps {
  articlesThisMonth: number;
  articlesLastMonth?: number;
  toolsThisMonth: number;
  toolsLastMonth?: number;
  mostUsedTool?: string | null;
  currentStreak: number;
  badgesEarned: number;
  totalBadges: number;
}

interface Insight {
  icon: React.ReactNode;
  message: string;
  type: 'positive' | 'neutral' | 'encouragement';
  link?: string;
  linkText?: string;
}

export default function UsageInsights({
  articlesThisMonth,
  articlesLastMonth = 0,
  toolsThisMonth,
  toolsLastMonth = 0,
  mostUsedTool,
  currentStreak,
  badgesEarned,
  totalBadges,
}: UsageInsightsProps) {
  const generateInsights = (): Insight[] => {
    const insights: Insight[] = [];

    // Article reading insights
    if (articlesThisMonth > 0) {
      if (articlesLastMonth > 0) {
        const diff = articlesThisMonth - articlesLastMonth;
        if (diff > 0) {
          insights.push({
            icon: <BookOpen className="w-4 h-4" />,
            message: `You've read ${articlesThisMonth} articles this month - ${diff} more than last month!`,
            type: 'positive',
          });
        } else if (diff < 0) {
          insights.push({
            icon: <BookOpen className="w-4 h-4" />,
            message: `You've read ${articlesThisMonth} articles this month. Keep reading to match last month's ${articlesLastMonth}!`,
            type: 'encouragement',
          });
        } else {
          insights.push({
            icon: <BookOpen className="w-4 h-4" />,
            message: `You've read ${articlesThisMonth} articles this month - same as last month!`,
            type: 'neutral',
          });
        }
      } else {
        insights.push({
          icon: <BookOpen className="w-4 h-4" />,
          message: `You've read ${articlesThisMonth} article${articlesThisMonth !== 1 ? 's' : ''} this month!`,
          type: 'positive',
        });
      }
    } else {
      insights.push({
        icon: <BookOpen className="w-4 h-4" />,
        message: `Start your month by reading an article!`,
        type: 'encouragement',
      });
    }

    // Tool usage insights
    if (toolsThisMonth > 0) {
      if (mostUsedTool) {
        insights.push({
          icon: <Wrench className="w-4 h-4" />,
          message: `You've used ${toolsThisMonth} tool${toolsThisMonth !== 1 ? 's' : ''} this month. ${mostUsedTool} is your favorite!`,
          type: 'positive',
        });
      } else {
        insights.push({
          icon: <Wrench className="w-4 h-4" />,
          message: `You've used ${toolsThisMonth} tool${toolsThisMonth !== 1 ? 's' : ''} this month!`,
          type: 'positive',
        });
      }
    } else {
      insights.push({
        icon: <Wrench className="w-4 h-4" />,
        message: `Explore our tools to solve engineering challenges faster!`,
        type: 'encouragement',
        link: '/tools',
        linkText: 'Browse Tools →',
      });
    }

    // Streak insight
    if (currentStreak >= 7) {
      insights.push({
        icon: <Calendar className="w-4 h-4" />,
        message: `Amazing! You're on a ${currentStreak}-day streak! 🔥`,
        type: 'positive',
      });
    } else if (currentStreak >= 3) {
      insights.push({
        icon: <Calendar className="w-4 h-4" />,
        message: `Nice ${currentStreak}-day streak! Keep it going for a week!`,
        type: 'positive',
      });
    }

    // Badge progress insight
    const badgePercent = Math.round((badgesEarned / totalBadges) * 100);
    if (badgePercent >= 50) {
      insights.push({
        icon: <Award className="w-4 h-4" />,
        message: `You've earned ${badgesEarned} of ${totalBadges} badges (${badgePercent}%)! 🏆`,
        type: 'positive',
      });
    } else if (badgesEarned > 0) {
      insights.push({
        icon: <Award className="w-4 h-4" />,
        message: `${badgesEarned} badge${badgesEarned !== 1 ? 's' : ''} earned! ${totalBadges - badgesEarned} more to collect.`,
        type: 'neutral',
      });
    }

    return insights;
  };

  const insights = generateInsights();

  const typeStyles = {
    positive: 'bg-green-500/10 border-green-500/20 text-green-400',
    neutral: 'bg-cream/5 border-border-muted text-foreground-secondary',
    encouragement: 'bg-accent-muted border-border text-accent',
  };

  return (
    <div className="space-y-3">
      {insights.map((insight, index) => (
        <div
          key={index}
          className={`
            flex items-start gap-3 p-3 rounded-lg border
            ${typeStyles[insight.type]}
          `}
        >
          <div className="mt-0.5">{insight.icon}</div>
          <div className="flex-1">
            <p className="text-sm">{insight.message}</p>
            {insight.link && (
              <Link
                href={insight.link}
                className="text-sm font-medium text-accent hover:text-accent transition-colors mt-1 inline-block"
              >
                {insight.linkText || 'Learn more →'}
              </Link>
            )}
          </div>
        </div>
      ))}

      {/* Overall trend indicator */}
      {articlesThisMonth > articlesLastMonth && toolsThisMonth > toolsLastMonth && (
        <div className="flex items-center gap-2 text-sm text-green-400 mt-4">
          <TrendingUp className="w-4 h-4" />
          <span>You&apos;re more active this month than last month!</span>
        </div>
      )}
    </div>
  );
}
